import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'

const money = minor => `£${(Number(minor || 0) / 100).toFixed(2)}`
const labels = {
  retirement_pending: 'Correction saved. Original payment retirement is pending. Revised report and payment remain blocked.',
  retirement_failed: 'Correction saved. Original payment retirement failed or is waiting for a scheduled retry. Revised report and payment remain blocked.',
  requires_review: 'Correction held for review. Do not send a replacement payment request.',
  ready: 'Original request retired. Continue to apply the saved correction.',
  applied: 'Correction applied. The revised report and payment request are ready for your explicit review and send. The payment request can include the corrected report; no separate report email is required. Nothing was emailed automatically.',
}
export default function GeneralIssuedPriceCorrection({ jobId, workshopId, dirty, disabled, onApplied }) {
  const [context, setContext] = useState(null)
  const [type, setType] = useState('percent')
  const [value, setValue] = useState('10')
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [retry, setRetry] = useState(null)
  const scope = `${workshopId}/${jobId}`
  const current = useRef(scope)
  current.current = scope
  const alive = useRef(true), inFlight = useRef(false), sequence = useRef(0)
  const notify = useRef(onApplied); notify.current = onApplied
  const valid = key => alive.current && current.current === key
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])
  async function load(key = scope) {
    const n = ++sequence.current
    const { data, error: failure } = await supabase.rpc('general_issued_price_correction_context', { p_workshop_id: workshopId, p_job_id: jobId })
    if (!valid(key) || n !== sequence.current) return
    if (failure) throw failure
    if (data?.job?.id !== jobId || !data.state) throw Error('Saved correction status is unavailable.')
    setContext(data)
    if (data.correction_id) setRetry(null)
    if (data.applied) notify.current?.()
  }
  useEffect(() => {
    sequence.current++; inFlight.current = false; setBusy(false); setContext(null); setRetry(null); setError('')
    load(scope).catch(e => { if (valid(scope)) setError(e.message) })
  }, [scope])
  const original = context?.original_minor ?? context?.payable?.payable_amount_pence ?? 0
  const discount = context?.discount_minor ?? (type === 'percent' ? Math.round(original * Number(value) / 100) : Math.round(Number(value) * 100))
  const invalid = !/^[0-9]+(\.[0-9]{1,2})?$/.test(value) || discount <= 0 || discount >= original || (type === 'percent' && Number(value) > 100) || reason.trim().length < 5
  const locked = busy || disabled || dirty || !context
  async function execute() {
    if (inFlight.current || locked) return
    const key = scope
    const request = retry || (context.correction_id
      ? { action: 'continue', workshopId, jobId, commandId: context.correction_id }
      : { action: 'save', workshopId, jobId, commandId: crypto.randomUUID(), command: { financial_revision_key: context.job.financial_revision_key, value_type: type, value, reason: reason.trim() } })
    inFlight.current = true; setBusy(true); setRetry(request); setError('')
    try {
      const { data: session } = await supabase.auth.getSession()
      const response = await fetch('/.netlify/functions/correct-general-issued-price', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.session?.access_token || ''}` }, body: JSON.stringify(request),
      })
      const body = await response.json()
      if (!response.ok) throw Error(body.error || 'Correction result could not be confirmed.')
      if (!valid(key)) return
      if (body.context?.job?.id !== jobId || body.context.correction_id !== request.commandId) throw Error('Correction response does not match this saved request.')
      setContext(body.context); setRetry(null); setError(body.retirement_error || '')
      if (body.context.applied) notify.current?.()
    } catch (e) { if (valid(key)) setError(`${e.message} Reload the saved status or retry the same request.`) }
    finally { if (valid(key)) { inFlight.current = false; setBusy(false) } }
  }
  return <section aria-label="Correct issued General price" style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
    <h3>Correct issued price</h3>
    <p>Owner-only whole-job discount. Original report, issued amount and delivery history are retained.</p>
    {dirty && <div className="alert alert-info">Save or discard your unsaved job changes before correcting issued pricing.</div>}
    {context && <p>Original {money(original)} · Discount −{money(discount)} · Revised {money(original - discount)}</p>}
    {context?.state !== 'available' && <div role="status" className="alert alert-info">{labels[context?.state] || 'Loading saved correction…'} {context?.blocked_reason}</div>}
    {error && <div role="alert" className="alert alert-error">{error}</div>}
    {context?.state === 'available' && <fieldset disabled={locked || Boolean(retry)} style={{ border: 0, padding: 0 }}>
      <div className="grid-2">
        <label className="field" style={{ display: 'grid', gap: 5, marginBottom: 14 }}>Correction discount type<select value={type} onChange={e => setType(e.target.value)}><option value="percent">Percentage of whole job</option><option value="fixed">Fixed GBP</option></select></label>
        <label className="field" style={{ display: 'grid', gap: 5, marginBottom: 14 }}>Correction discount value<input type="number" min="0.01" step="0.01" value={value} onChange={e => setValue(e.target.value)} /></label>
      </div>
      <label className="field" style={{ display: 'grid', gap: 5, marginBottom: 14 }}>Owner correction reason<textarea value={reason} onChange={e => setReason(e.target.value)} /></label>
      <button type="button" className="btn btn-primary btn-sm" disabled={invalid} onClick={execute}>Save correction and retire old request</button>
    </fieldset>}
    {retry ? <button type="button" className="btn btn-primary btn-sm" disabled={locked} onClick={execute}>Retry same correction request</button>
      : ['retirement_pending', 'retirement_failed', 'ready'].includes(context?.state) && <button type="button" className="btn btn-primary btn-sm" disabled={locked} onClick={execute}>Continue saved correction</button>}
    <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => load().catch(e => { if (valid(scope)) setError(e.message) })}>Reload correction status</button>
  </section>
}
